import { last } from 'lodash';

import { SemboDate } from '@libs/sembo-dates';
import type {
  ActivityIngredientAdapter,
  ActivityIngredientResponse,
  ActivityIngredientResponseByItineraryQL,
  AvailableTransportIngredientResponse,
  AvailableTransportIngredientResponseQL,
  FerryIngredientAdapter,
  FerryIngredientResponse,
  FlightIngredientAdapter,
  FlightIngredientResponse,
  HotelBookableOption,
  HotelBookableOptionAlternativeRoom,
  HotelBookableOptionWithAlternativeRoom,
  HotelBookableOptionWithBasicRooms,
  HotelBookableOptionWithPax,
  HotelBookableRoom,
  HotelIngredientAdapter,
  HotelIngredientResponse,
  HotelIngredientResponseByItineraryQL,
  OwnStayIngredientAdapter,
  OwnStayIngredientResponse,
  OwnStayIngredientResponseByItineraryQL,
  Pax,
  PaxQL,
  RoomPax,
  TransportAlternative,
  TransportIngredientResponseQL,
  TransportLeg,
  TransportResultQL,
  UnavailableHotelIngredientResponse,
  UnavailableTransportIngredientResponse,
  UnavailableTransportIngredientResponseQL,
} from '@libs/trip-planner-interfaces';
import {
  AvailabilityOrder,
  AvailabilityOrderQL,
  IngredientType,
  TransportVehicleType,
  TransportVehicleTypeQL,
} from '@libs/trip-planner-interfaces';
import { assertValue } from '@libs/utils';

import { isAvailableTransportIngredientResponseQL } from './typeGuards';
import { isSameStringContent, mapEnumValue, normalizeDate } from './utils';

export function toOwnStayIngredientResponse(
  result: OwnStayIngredientResponseByItineraryQL,
  ingredient: OwnStayIngredientAdapter
): OwnStayIngredientResponse {
  return {
    date: new Date(result.date),
    ingredientId: ingredient.ingredientId,
    ingredientType: IngredientType.OwnStay,
    isAvailable: true,
    numberOfNights: result.numberOfNights,
    isNumberOfNightsSuggested: !ingredient.numberOfNights,
    subIngredientResponse:
      result.subIngredientResults?.map((subResult, subIndex) => {
        const subIngredient = ingredient.subIngredients[subIndex];

        assertValue(
          subIngredient,
          'search-service-smart-search > mappers.ts > toOwnStayIngredientResponse > subIngredient'
        );

        return toActivityIngredientResponse(subResult, subIngredient);
      }) ?? [],
  };
}

export function toHotelIngredientResponse(
  result: HotelIngredientResponseByItineraryQL,
  ingredient: HotelIngredientAdapter
): HotelIngredientResponse {
  const isAvailable = !!result.suggested;

  const suggested = isAvailable
    ? getSuggestedFromResponse(result.suggested)
    : null;

  const unavailableHotelIngredientResponse =
    toUnavailableHotelIngredientResponse(result, ingredient);

  if (suggested) {
    return {
      ...unavailableHotelIngredientResponse,
      isAvailable: true,
      suggested: suggested.withPax,
      suggestions: result.suggestions ? result.suggestions : [],
      options: [suggested.basic],
      addToBasketToken: suggested.basic.addToBasketToken,
      alternativeSuggestion: null,
    };
  } else {
    return unavailableHotelIngredientResponse;
  }
}

function getSuggestedFromResponse(
  responseSuggested:
    | HotelBookableOptionWithBasicRooms
    | HotelBookableOptionWithAlternativeRoom
): {
  basic: HotelBookableOption;
  withPax: HotelBookableOptionWithPax | HotelBookableOptionWithAlternativeRoom;
} {
  const rooms: (HotelBookableOptionAlternativeRoom | HotelBookableRoom)[] =
    responseSuggested.rooms;
  const hotelBookableOption: HotelBookableOption = {
    addToBasketToken: responseSuggested.addToBasketToken,
    code: responseSuggested.code,
    totalPrice: responseSuggested.totalPrice,
    isRefundable: responseSuggested.isRefundable,
  };

  const pax: Pax = rooms.reduce(
    (
      totalPax: Pax,
      room: HotelBookableOptionAlternativeRoom | HotelBookableRoom
    ) => {
      const roomPax: RoomPax = room.pax.reduce(
        (roomPax: RoomPax, currentRoomPax: PaxQL) => {
          if (currentRoomPax.age === null) {
            roomPax.numberOfAdults++;
          } else {
            roomPax.childAges
              ? roomPax.childAges.push(currentRoomPax.age)
              : (roomPax.childAges = [currentRoomPax.age]);
          }
          return roomPax;
        },
        { numberOfAdults: 0 }
      );
      totalPax.push(roomPax);
      return totalPax;
    },
    []
  );

  return {
    basic: hotelBookableOption,
    withPax: {
      ...hotelBookableOption,
      pax,
      rooms: rooms.map((roomObject) => ({
        description: roomObject.description,
        cancellationPolicies: roomObject.cancellationPolicies,
        taxesAndFees: roomObject.taxesAndFees,
        price: roomObject.price,
      })),
    },
  };
}

function toUnavailableHotelIngredientResponse(
  result: HotelIngredientResponseByItineraryQL,
  ingredient: HotelIngredientAdapter
): UnavailableHotelIngredientResponse {
  return {
    suggested: null,
    suggestions: null,
    options: [],
    addToBasketToken: null,
    date: new Date(result.date),
    numberOfNights: result.lengthOfStay.numberOfNights,
    isNumberOfNightsSuggested: result.lengthOfStay.nightsHaveBeenSuggested,
    ingredientType: IngredientType.Hotel,
    ingredientId: ingredient.ingredientId,
    subIngredientResponse:
      result.subIngredientResults?.map((subResult, subIndex) => {
        const subIngredient = ingredient.subIngredients[subIndex];

        assertValue(
          subIngredient,
          'search-service-smart-search > mappers.ts > toHotelIngredientResponse > subIngredient'
        );

        return toActivityIngredientResponse(subResult, subIngredient);
      }) ?? [],
    isAvailable: false,
    alternativeSuggestion: result.alternativeSuggestion
      ? {
          ...result.alternativeSuggestion,
          pax: getSuggestedFromResponse(result.alternativeSuggestion).withPax
            .pax,
        }
      : null,
  };
}

export function toActivityIngredientResponse(
  result: ActivityIngredientResponseByItineraryQL,
  ingredient: ActivityIngredientAdapter
): ActivityIngredientResponse {
  const isAvailable = !!result.totalPrice;

  const suggested = isAvailable
    ? {
        code: result.code,
        totalPrice: result.totalPrice,
        addToBasketToken: '',
      }
    : null;

  return {
    ingredientId: ingredient.parentIngredientId,
    ingredientType: IngredientType.Activity,
    date: new Date(result.date),
    options: [],
    subIngredientResponse: [],
    suggested,
    isAvailable: !!result.totalPrice,
  };
}

export function toFlightIngredientResponse(
  result: TransportIngredientResponseQL | TransportResultQL,
  ingredient: FlightIngredientAdapter
): FlightIngredientResponse {
  if (isAvailableTransportIngredientResponseQL(result)) {
    const availableTransportIngredientResponse =
      toAvailableTransportIngredientResponse(result, ingredient);

    const leg = availableTransportIngredientResponse.legs[result.legIndex];

    assertValue(
      leg,
      'search-service-smart-search > mappers.ts > toFlightIngredientResponse > leg'
    );

    return {
      numberOfStops: leg.segments.length - 1,
      ...availableTransportIngredientResponse,
    };
  }

  return {
    legs: null,
    numberOfStops: 0,
    ...toUnavailableTransportIngredientResponse(result, ingredient),
  };
}

export function toFerryIngredientResponse(
  result: TransportIngredientResponseQL | TransportResultQL,
  ingredient: FerryIngredientAdapter
): FerryIngredientResponse {
  if (isAvailableTransportIngredientResponseQL(result)) {
    const availableTransportIngredientResponse =
      toAvailableTransportIngredientResponse(result, ingredient);

    const leg = availableTransportIngredientResponse.legs[result.legIndex];

    assertValue(
      leg,
      'search-service-smart-search > mappers.ts > toFerryIngredientResponse > leg'
    );

    const firstSegment = leg.segments[0];

    assertValue(
      firstSegment,
      'search-service-smart-search > mappers.ts > toFerryIngredientResponse > firstSegment'
    );

    const vehicles = firstSegment.vehicles;

    if (!vehicles) {
      throw new Error('FerryIngredientResponse should always have vehicles');
    }

    const transportationName = firstSegment.transportationName;
    const isCabinMandatory = firstSegment.isCabinMandatory;

    return {
      vehicles,
      transportationName,
      isCabinMandatory,
      ...availableTransportIngredientResponse,
    };
  }

  return {
    vehicles: null,
    transportationName: null,
    isCabinMandatory: null,
    ...toUnavailableTransportIngredientResponse(result, ingredient),
    alternativeSuggestion: null,
  };
}

// TODO Split fligth itinerary logic from flight list logic
function toAvailableTransportIngredientResponse(
  result: AvailableTransportIngredientResponseQL | TransportResultQL,
  ingredient: FlightIngredientAdapter | FerryIngredientAdapter
): AvailableTransportIngredientResponse {
  const leg = result.legs[result.legIndex];
  assertValue(
    leg,
    'search-service-smart-search > mappers.ts > toAvailableTransportIngredientResponse > leg'
  );

  const firstSegment = leg.segments[0];
  const lastSegment = last(leg.segments);

  if (!firstSegment || !lastSegment) {
    throw new Error('Leg segment not found');
  }

  const transportIds: string[] = [];
  const carriers: string[] = [];

  leg.segments.forEach((segment) => {
    transportIds.push(segment.transportId);
    carriers.push(segment.carrier);
  });

  const date = normalizeDate(
    new SemboDate(firstSegment.departureDateTime).startOf('day').get()
  );

  const legs: TransportLeg[] = result.legs.map((leg) => ({
    ...leg,
    segments: leg.segments.map((segment) => ({
      ...segment,
      vehicles: segment.vehicles
        ? segment.vehicles?.map((vehicle) => toTransportVehicleType(vehicle))
        : undefined,
    })),
  }));

  return {
    ingredientId: ingredient.ingredientId,
    ingredientType: ingredient.ingredientType,
    addToBasketToken: result.addToBasketToken,
    transportIds,
    carriers,
    allCarriers:
      'allCarriers' in result && !!result.allCarriers ? result.allCarriers : [],
    date,
    departureDate: new Date(firstSegment.departureDateTime),
    departureIata: firstSegment.departureIata,
    arrivalDate: new Date(lastSegment.arrivalDateTime),
    arrivalIata: lastSegment.arrivalIata,
    totalPrice: result.totalPrice,
    durationInMinutes: leg.durationInMinutes,
    durationRangeInMinutes:
      'durationRangeInMinutes' in result
        ? result.durationRangeInMinutes
        : undefined,
    legs,
    legIndex: result.legIndex,
    isAvailable: !!result.totalPrice,
    alternativeSuggestion: null,
  };
}

function toUnavailableTransportIngredientResponse(
  result: UnavailableTransportIngredientResponseQL,
  ingredient: FlightIngredientAdapter | FerryIngredientAdapter
): UnavailableTransportIngredientResponse {
  const alternativeSuggestion = result.alternativeSuggestion
    ? toAlternativeTransportSuggestion(result.alternativeSuggestion)
    : null;

  if (!alternativeSuggestion) {
    return {
      ingredientId: ingredient.ingredientId,
      ingredientType: ingredient.ingredientType,
      date: new Date(result.date),
      totalPrice: result.totalPrice,
      isAvailable: !!result.totalPrice,
      addToBasketToken: null,
      alternativeSuggestion: null,
    };
  }

  const leg = alternativeSuggestion.legs[result.legIndex];
  assertValue(
    leg,
    'search-service-smart-search > mappers.ts > toAvailableTransportIngredientResponse > leg'
  );

  const firstSegment = leg.segments[0];

  if (!firstSegment) {
    throw new Error('Leg segment not found');
  }

  const date = normalizeDate(
    new SemboDate(firstSegment.departureDateTime).startOf('day').get()
  );

  return {
    ingredientId: ingredient.ingredientId,
    ingredientType: ingredient.ingredientType,
    addToBasketToken: null,
    date,
    totalPrice: result.totalPrice,
    isAvailable: !!result.totalPrice,
    alternativeSuggestion,
  };
}

export function toAlternativeTransportSuggestion(
  alternativeSuggestion: TransportResultQL
): TransportAlternative {
  const legs: TransportLeg[] = alternativeSuggestion.legs.map((leg) => ({
    ...leg,
    segments: leg.segments.map((segment) => ({
      ...segment,
      vehicles: segment.vehicles
        ? segment.vehicles?.map((vehicle) => toTransportVehicleType(vehicle))
        : undefined,
    })),
  }));

  const leg = alternativeSuggestion.legs[alternativeSuggestion.legIndex];

  assertValue(
    leg,
    'search-service-smart-search > mappers.ts > toAlternativeTransportSuggestion > leg'
  );

  const firstSegment = leg.segments[0];
  const lastSegment = last(leg.segments);

  if (!firstSegment || !lastSegment) {
    throw new Error('Alternative leg segment not found');
  }

  const transportIds: string[] = [];
  const carriers: string[] = [];

  leg.segments.forEach((segment) => {
    transportIds.push(segment.transportId);
    carriers.push(segment.carrier);
  });

  return {
    ...alternativeSuggestion,
    legs,
    transportIds,
    carriers,
    departureIata: firstSegment.departureIata,
    departureDate: firstSegment.departureDateTime,
    arrivalIata: lastSegment.arrivalIata,
    arrivalDate: lastSegment.arrivalDateTime,
    durationInMinutes: leg.durationInMinutes,
    numberOfStops: leg.segments.length - 1,
  };
}

export function toTransportVehicleType(
  vehicleQL: TransportVehicleTypeQL
): TransportVehicleType {
  if (!vehicleQL) {
    return TransportVehicleType.None;
  }

  // TODO: Remove isSameStringContent once SmartSearch already returns enum values in CONSTANT_CASE
  return mapEnumValue(
    TransportVehicleTypeQL,
    TransportVehicleType,
    vehicleQL,
    (value) => isSameStringContent(value, vehicleQL)
  );
}

export function toAvailabilityOrder(
  availabilityOrderQL: AvailabilityOrderQL
): AvailabilityOrder {
  return mapEnumValue(
    AvailabilityOrderQL,
    AvailabilityOrder,
    availabilityOrderQL,
    (value) => isSameStringContent(value, availabilityOrderQL)
  );
}
